import { useEffect, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useLocale } from '../../i18n/LocaleContext'
import Header from './Header'
import Footer from './Footer'
import SEOHead from '../SEOHead'
import StructuredData from '../StructuredData'
import FloatingCallButton from '../FloatingCallButton'
import { pushDataLayer } from '../../lib/analytics'

export default function Layout({ children }: { children: ReactNode }) {
  const { locale } = useLocale()
  const { pathname } = useLocation()

  // Set html lang attribute
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  // SPA page_view: GA4 only auto-tracks the first load, so push a page_view to
  // the dataLayer on every client-side navigation. Runs after the page's
  // SEOHead has updated document.title (child effects fire before this parent
  // effect), so page_title is current. Add a GTM Custom Event trigger on
  // 'page_view' + a GA4 Event tag — and DISABLE GA4 Enhanced Measurement page
  // views to avoid double-counting.
  useEffect(() => {
    pushDataLayer({
      event: 'page_view',
      page_location: window.location.href,
      page_path: pathname + window.location.search,
      page_title: document.title,
      language: locale,
    })
  }, [pathname, locale])

  return (
    <>
      <SEOHead />
      <StructuredData />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:bg-primary focus:text-white focus:px-4 focus:py-2 focus:rounded-btn focus:font-medium"
      >
        {locale === 'hu' ? 'Ugrás a tartalomra' : 'Sari la conținut'}
      </a>
      <Header />
      {/* key=pathname remounts on navigation so the fade-in replays each route. */}
      <main
        id="main-content"
        key={pathname}
        className="pt-16 md:pt-20 motion-safe:animate-[pageFade_0.35s_ease-out]"
      >
        {children}
      </main>
      <Footer />
      <FloatingCallButton />
    </>
  )
}
